import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Input, Button, Modal, Tooltip, Tag, Spin, Empty,
} from "antd";
import {
  SearchOutlined, EyeOutlined, DownloadOutlined,
  UserOutlined, FileTextOutlined, FileExcelOutlined,
  CheckCircleOutlined, ClockCircleOutlined, IdcardOutlined,
} from "@ant-design/icons";
import { useVendorMasterStore } from "../../../store/useVendorMasterStore";
import useTabsSystemStore from "../../../store/RenderTabStore";
import { useThemeStore } from "../../../store/useThemeStore";
import { colSearch, actionBtnStyle, exportCSV, exportExcel } from "../../Master/masterUtils";
import userManualPdf from "../../../assets/sampleFiles/Vendor_Portal_User_Manual.pdf";

/* ── helpers ──────────────────────────────────────────────────────────────── */
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

function StatusBadge({ value }) {
  const submitted = String(value).toLowerCase() === "yes";
  return (
    <Tag
      icon={submitted ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
      color={submitted ? "success" : "warning"}
      style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, padding: "1px 10px", margin: 0, border: "none" }}
    >
      {submitted ? "Submitted" : "Pending"}
    </Tag>
  );
}

function ApprovalBadge({ status }) {
  const s = String(status ?? "").toLowerCase();
  if (s === "approved")   return <Tag color="success" style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, border: "none" }}>Approved</Tag>;
  if (s === "rejected")   return <Tag color="error"   style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, border: "none" }}>Rejected</Tag>;
  if (s === "pending")    return <Tag color="warning" style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, border: "none" }}>Pending</Tag>;
  return <Tag style={{ borderRadius: 20, fontSize: 11, border: "none" }}>{status || "—"}</Tag>;
}

/* ── main component ────────────────────────────────────────────────────────── */
const RegistrationGrid = () => {
  const { themeColor } = useThemeStore();
  const rgb = hexToRgb(themeColor);

  const { getVendorProfile, vendorProfiles, getApprovedStatus, approvedStatus, loading } = useVendorMasterStore();
  const { openTabs, addNewTab, setActiveTab } = useTabsSystemStore();

  const [search,       setSearch]       = useState("");
  const [pagination,   setPagination]   = useState({ current: 1, pageSize: 10 });
  const [modalOpen,    setModalOpen]    = useState(false);
  const [modalGuid,    setModalGuid]    = useState(null);
  const [statusLoading,setStatusLoading]= useState(false);
  const [manualLoading,setManualLoading]= useState(false);

  useEffect(() => {
    getVendorProfile();
  }, []);

  /* set ShowStep4 localStorage */
  useEffect(() => {
    if (Array.isArray(vendorProfiles) && vendorProfiles.length > 0) {
      const hasSubmittedKey = vendorProfiles.some(item => Object.prototype.hasOwnProperty.call(item, "Submitted"));
      localStorage.setItem("ShowStep4", hasSubmittedKey ? "No" : "Yes");
    } else {
      localStorage.setItem("ShowStep4", "Yes");
    }
  }, [vendorProfiles]);

  /* ── open registration form ─────────────────────────────────────────────── */
  function openRegistration(row) {
    localStorage.setItem("vendorGUID", row.VendorGUID);
    localStorage.setItem("submitted",  row.Submitted);
    localStorage.setItem("VendorType", row.VendorType);
    addNewTab([...openTabs, { closable: true, key: "new_registration", label: "New Vendor Registration" }]);
    setActiveTab("new_registration");
  }

  /* ── view approval status ───────────────────────────────────────────────── */
  async function viewStatus(row) {
    setModalGuid(row.VendorGUID);
    setStatusLoading(true);
    setModalOpen(true);
    await getApprovedStatus(row.VendorGUID);
    setStatusLoading(false);
  }

  /* ── user manual download ───────────────────────────────────────────────── */
  function handleUserManualDownload() {
    setManualLoading(true);
    const link = document.createElement("a");
    link.href = userManualPdf; link.download = "User_Manual.pdf";
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    setManualLoading(false);
  }

  /* ── derived stats ──────────────────────────────────────────────────────── */
  const totalVendors    = vendorProfiles?.length ?? 0;
  const submittedCount  = vendorProfiles?.filter(r => String(r.Submitted).toLowerCase() === "yes").length ?? 0;
  const pendingCount    = totalVendors - submittedCount;

  /* ── filtered data ──────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !Array.isArray(vendorProfiles)) return vendorProfiles ?? [];
    return vendorProfiles.filter(r =>
      r.VendorGUID?.toLowerCase().includes(q) ||
      r.VendorName?.toLowerCase().includes(q) ||
      r.EmailID?.toLowerCase().includes(q) ||
      r.VendorType?.toLowerCase().includes(q) ||
      r.DivisionName?.toLowerCase().includes(q)
    );
  }, [vendorProfiles, search]);

  /* ── table columns ──────────────────────────────────────────────────────── */
  const columns = [
    {
      title: "S.No", key: "sno", width: 60, align: "center",
      render: (_, __, i) => (
        <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>
          {(pagination.current - 1) * pagination.pageSize + i + 1}
        </span>
      ),
    },
    {
      title: "GUID", dataIndex: "VendorGUID", key: "VendorGUID", width: 160,
      ...colSearch("VendorGUID", "GUID", themeColor),
      render: (v, row) => (
        <button
          onClick={() => openRegistration(row)}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            color: themeColor, fontSize: 12, fontWeight: 700,
            textDecoration: "underline", textDecorationStyle: "dotted",
            fontFamily: "monospace", letterSpacing: "0.02em",
          }}
        >
          {v}
        </button>
      ),
    },
    {
      title: "Vendor Name", dataIndex: "VendorName", key: "VendorName",
      sorter: (a, b) => (a.VendorName ?? "").localeCompare(b.VendorName ?? ""),
      ...colSearch("VendorName", "Vendor Name", themeColor),
      render: (v, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: `rgba(${rgb},0.10)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <UserOutlined style={{ fontSize: 12, color: themeColor }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>{v || "—"}</p>
            {row.EmailID && <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{row.EmailID}</p>}
          </div>
        </div>
      ),
    },
    {
      title: "Division", dataIndex: "DivisionName", key: "DivisionName", width: 130,
      ...colSearch("DivisionName", "Division", themeColor),
      render: v => <span style={{ fontSize: 12, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: "Department", dataIndex: "DepartmentName", key: "DepartmentName", width: 130,
      ...colSearch("DepartmentName", "Department", themeColor),
      render: v => <span style={{ fontSize: 12, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: "Type", dataIndex: "VendorType", key: "VendorType", width: 100, align: "center",
      render: v => v ? (
        <span style={{ fontSize: 11, fontWeight: 700, color: themeColor, background: `rgba(${rgb},0.10)`, padding: "2px 10px", borderRadius: 20 }}>{v}</span>
      ) : "—",
    },
    {
      title: "Status", dataIndex: "Submitted", key: "Submitted", width: 120, align: "center",
      filters: [{ text: "Submitted", value: "yes" }, { text: "Pending", value: "pending" }],
      onFilter: (val, rec) => val === "yes"
        ? String(rec.Submitted).toLowerCase() === "yes"
        : String(rec.Submitted).toLowerCase() !== "yes",
      render: v => <StatusBadge value={v} />,
    },
    {
      title: "Requested On", dataIndex: "RequestedOn", key: "RequestedOn", width: 130, align: "center",
      render: v => v ? (
        <span style={{ fontSize: 11, color: "#6b7280" }}>
          {new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
      ) : "—",
    },
    {
      title: "Action", key: "action", width: 70, align: "center", fixed: "right",
      render: (_, row) => (
        <Tooltip title="View Approval Status" placement="top">
          <button
            onClick={() => viewStatus(row)}
            style={actionBtnStyle(themeColor)}
            onMouseEnter={e => Object.assign(e.currentTarget.style, { background: `rgba(${rgb},0.08)`, borderColor: themeColor + "44" })}
            onMouseLeave={e => Object.assign(e.currentTarget.style, { background: "#f9fafb", borderColor: "#e5e7eb" })}
          >
            <EyeOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      ),
    },
  ];

  /* ── export rows ────────────────────────────────────────────────────────── */
  const exportRows = filtered.map((r, i) => ({
    "S.No": i + 1, "GUID": r.VendorGUID, "Vendor Name": r.VendorName,
    "Division": r.DivisionName, "Department": r.DepartmentName,
    "Type": r.VendorType, "Email": r.EmailID,
    "Status": String(r.Submitted).toLowerCase() === "yes" ? "Submitted" : "Pending",
    "Requested On": r.RequestedOn,
  }));

  /* ── approval status modal table columns ────────────────────────────────── */
  const statusColumns = [
    { title: "#", key: "idx", width: 50, align: "center", render: (_, __, i) => <span style={{ fontSize: 12, color: "#9ca3af" }}>{i + 1}</span> },
    { title: "Vendor Name", dataIndex: "VendorName",  key: "VendorName",  render: v => <span style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{v}</span> },
    { title: "Assigned By",  dataIndex: "Assigner",    key: "Assigner",    render: v => <span style={{ fontSize: 12, color: "#374151" }}>{v}</span> },
    { title: "Assigned On",  dataIndex: "AssignOn",    key: "AssignOn",    render: v => <span style={{ fontSize: 11, color: "#6b7280" }}>{v}</span> },
    { title: "Assigned To",  dataIndex: "AssignTo",    key: "AssignTo",    render: v => <span style={{ fontSize: 12, color: "#374151" }}>{v}</span> },
    { title: "Status",       dataIndex: "VendorStatus", key: "VendorStatus", align: "center", render: v => <ApprovalBadge status={v} /> },
    { title: "Status On",    dataIndex: "StatusOn",    key: "StatusOn",    render: v => <span style={{ fontSize: 11, color: "#6b7280" }}>{v}</span> },
    { title: "Remark",       dataIndex: "StatusRemark", key: "StatusRemark", render: v => <span style={{ fontSize: 12, color: "#374151" }}>{v || "—"}</span> },
  ];

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 14, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "12px 18px",
        marginBottom: 10, display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: themeColor, display: "flex", alignItems: "center",
            justifyContent: "center", boxShadow: `0 4px 14px ${themeColor}44`,
            transition: "background 0.3s",
          }}>
            <IdcardOutlined style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Vendor Profile</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
              {[
                { val: totalVendors,   label: "Total",     color: themeColor },
                { val: submittedCount, label: "Submitted", color: "#16a34a" },
                { val: pendingCount,   label: "Pending",   color: "#d97706" },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <span style={{ width: 1, height: 12, background: "#e5e7eb", display: "inline-block" }} />}
                  <span style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.val}</span>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>{s.label}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* User Manual download */}
        <Button
          icon={<DownloadOutlined />} onClick={handleUserManualDownload}
          loading={manualLoading}
          style={{ height: 36, borderRadius: 9, fontWeight: 600, fontSize: 12, borderColor: "#dc2626", color: "#dc2626" }}
        >
          User Manual
        </Button>
      </div>

      {/* ── Table card ───────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f1f3", flexWrap: "wrap", gap: 8 }}>
          <Input
            placeholder="Search GUID, name, email, type…"
            prefix={<SearchOutlined style={{ color: "#c4c7cd" }} />}
            value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear style={{ width: 300, borderRadius: 8, height: 34 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            <Button icon={<FileTextOutlined />} onClick={() => exportCSV(exportRows, "vendor_profile.csv")} style={{ height: 30, borderRadius: 7, fontWeight: 600, borderColor: "#22c55e", color: "#16a34a", fontSize: 11 }}>CSV</Button>
            <Button icon={<FileExcelOutlined />} onClick={() => exportExcel(exportRows, "vendor_profile.xlsx")} style={{ height: 30, borderRadius: 7, fontWeight: 600, borderColor: "#16a34a", color: "#15803d", fontSize: 11 }}>Excel</Button>
          </div>
        </div>

        {/* Table */}
        <Table
          rowKey="VendorGUID"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          scroll={{ x: 1000 }}
          onChange={pag => setPagination({ current: pag.current, pageSize: pag.pageSize })}
          pagination={{
            ...pagination, total: filtered.length,
            showSizeChanger: true, pageSizeOptions: ["10", "20", "50"],
            showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`,
            style: { padding: "8px 14px", margin: 0 },
          }}
        />
      </div>

      {/* ── Approval Status Modal ────────────────────────────────────────── */}
      <Modal
        open={modalOpen} onCancel={() => setModalOpen(false)} footer={null}
        width={900} centered destroyOnClose
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `rgba(${rgb},0.10)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EyeOutlined style={{ fontSize: 14, color: themeColor }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>Approval Status</p>
              <p style={{ margin: "1px 0 0", fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{modalGuid}</p>
            </div>
          </div>
        }
      >
        {statusLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "48px" }}>
            <Spin size="large" />
          </div>
        ) : !approvedStatus?.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approval records found" style={{ padding: "40px 0" }} />
        ) : (
          <Table
            rowKey={(_, i) => i}
            columns={statusColumns}
            dataSource={approvedStatus}
            size="small"
            pagination={false}
            scroll={{ x: 700 }}
            style={{ marginTop: 4 }}
          />
        )}
      </Modal>
    </div>
  );
};

export default RegistrationGrid;
